import type { Node } from '@xyflow/react'
import type { MindMapNode, MindMapEdge } from '@bandos/shared'
import type { ReactFlowNodeData } from '../types/mindmap-components'

/**
 * Transient UI-only fields that should never be persisted to the backend.
 * Using a denylist (instead of an allowlist) ensures new data fields are
 * preserved by default — no more silent data loss when adding fields.
 */
const TRANSIENT_FIELDS: ReadonlySet<string> = new Set([
  'toolbarVisible',
  'mindmapId',
  'labels',
  'isGenerating',
  'generatingRatingNames',
  'isGeneratingStoryboard',
  'isGeneratingPortrait',
])

/**
 * Strip transient UI-only fields and undefined values from node data.
 */
function stripTransientFields(data: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(data)) {
    if (!TRANSIENT_FIELDS.has(key) && value !== undefined) {
      result[key] = value
    }
  }
  return result
}

/**
 * Convert React Flow node format to backend format.
 * Uses a denylist to strip transient UI fields — all other fields are preserved automatically.
 * Only one transformation is applied: phase is persisted only for idea nodes.
 */
export function toBackendNodeFormat(reactFlowNode: Node<ReactFlowNodeData>): MindMapNode {
  const data = stripTransientFields(reactFlowNode.data as Record<string, unknown>)

  // Only idea nodes have phases — need and venture are pre-phase nodes
  if (data.category !== 'idea') {
    delete data.phase
  }

  return {
    id: reactFlowNode.id,
    position: reactFlowNode.position,
    data: data as MindMapNode['data'],
    type: reactFlowNode.type ?? `${String(data.category)}Node`,
  }
}

/**
 * Convert backend edge format to React Flow edge format
 */
export function toBackendEdgeFormat(edge: {
  id: string
  source: string
  target: string
  sourceHandle?: string | null
  targetHandle?: string | null
  type?: string
  animated?: boolean
}): MindMapEdge {
  return {
    id: edge.id,
    source: edge.source,
    target: edge.target,
    sourceHandle: edge.sourceHandle ?? null,
    targetHandle: edge.targetHandle ?? null,
    type: edge.type,
    animated: edge.animated,
  }
}
